import { useState, useEffect, useRef } from "react";
import { Menu, X, Phone, ChevronDown, Facebook } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_austin-yard-pro/artifacts/h82kv0hn_IMG_4290.png";
const FACEBOOK_URL = "https://www.facebook.com/profile.php?id=61589893103496";

export default function Nav({ offsetTop = false }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const companyRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (companyRef.current && !companyRef.current.contains(e.target)) {
        setCompanyOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const links = [
    { href: "/#services", label: "Services" },
    { href: "/#work", label: "Work" },
    { href: "/#pricing", label: "Pricing" },
    { href: "/#about", label: "About" },
    { href: "/shop", label: "Shop" },
  ];

  const companyLinks = [
    { href: "/#meet-ceo", label: "Founder" },
    { href: "/partners", label: "Partners" },
    { href: "/affiliate", label: "Affiliate" },
  ];

  return (
    <header
      data-testid="site-nav"
      className={`fixed left-0 right-0 z-50 transition-all ${
        offsetTop ? "top-10 md:top-9" : "top-0"
      } ${
        scrolled ? "bg-black/80 backdrop-blur-xl border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="container-ic flex items-center justify-between py-4">
        <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
          <img
            src={LOGO}
            alt="Ironclad Yard Services"
            className="h-12 md:h-14 w-auto object-contain"
          />
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm uppercase tracking-widest transition-colors text-muted-foreground hover:text-primary"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}

          {/* Company dropdown */}
          <div className="relative" ref={companyRef}>
            <button
              type="button"
              onClick={() => setCompanyOpen((v) => !v)}
              onMouseEnter={() => setCompanyOpen(true)}
              className="flex items-center gap-1 text-sm uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors"
              data-testid="nav-company-toggle"
              aria-expanded={companyOpen}
            >
              Company
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform ${companyOpen ? "rotate-180" : ""}`}
              />
            </button>
            {companyOpen && (
              <div
                onMouseLeave={() => setCompanyOpen(false)}
                className="absolute left-1/2 -translate-x-1/2 top-full mt-3 min-w-[180px] bg-black/95 backdrop-blur-xl border border-border shadow-xl"
                data-testid="nav-company-menu"
              >
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-2 h-2 bg-black/95 border-l border-t border-border rotate-45" />
                {companyLinks.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setCompanyOpen(false)}
                    className="block px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors border-b border-border/40 last:border-b-0"
                    data-testid={`nav-company-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="/#booking"
            className="text-sm uppercase tracking-widest text-white font-bold hover:text-primary transition-colors"
            data-testid="nav-link-book"
          >
            Book
          </a>
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow Ironclad on Facebook"
            className="w-9 h-9 border border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center"
            data-testid="nav-facebook"
          >
            <Facebook className="h-4 w-4" />
          </a>
          <a
            href="tel:5129480676"
            className="flex items-center gap-2 text-sm text-white font-bold hover:text-primary transition-colors"
            data-testid="nav-phone"
          >
            <Phone className="h-4 w-4" /> 512-948-0676
          </a>
          <a href="/#booking" className="btn-primary" data-testid="nav-book-btn">
            Free Estimate
          </a>
        </div>

        <button
          className="md:hidden text-white"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-black" data-testid="mobile-menu">
          <div className="container-ic py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-base uppercase tracking-widest text-foreground py-2"
              >
                {l.label}
              </a>
            ))}

            {/* Mobile Company group */}
            <div className="py-2 border-t border-border/40 mt-2">
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-2">
                Company
              </div>
              <div className="flex flex-col gap-3 pl-2">
                {companyLinks.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="text-base uppercase tracking-widest text-foreground"
                    data-testid={`mobile-company-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </a>
                ))}
              </div>
            </div>

            <a
              href="/#booking"
              onClick={() => setOpen(false)}
              className="text-base uppercase tracking-widest text-foreground py-2"
            >
              Book
            </a>
            <a href="tel:5129480676" className="text-primary text-base mt-2">
              512-948-0676
            </a>
            <a href="/#booking" onClick={() => setOpen(false)} className="btn-primary w-full">
              Free Estimate
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
